import React, { useState, useEffect, useRef } from 'react';
import { 
  FiPackage, FiPlus, FiMinus, FiAlertCircle
} from "react-icons/fi";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Slide } from 'react-toastify';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import LeftMenu from '../components/LeftMenu';
import withAuth from '../common/auth';
import axios from 'axios';
import { BASE_URL, GET_ITEM_MASTER, GET_PRODUCT_BATCHES, POST_STOCK_ADJUSTMENT } from '../utlilities/config';

// Enhanced Spinner Component
const Spinner = () => (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-white bg-opacity-80 backdrop-blur-sm">
      <div className="flex flex-col items-center gap-3">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
        <p className="text-indigo-600 font-medium">Loading...</p>
      </div>
    </div>
);

const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: '#f9fafb',
    borderColor: state.isFocused ? '#6366f1' : '#b5b6b8ff',
    boxShadow: state.isFocused ? '0 0 0 1px #6366f1' : 'none',
    borderRadius: '0.5rem',
    '&:hover': {
      borderColor: '#6366f1',
    },
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? '#92c3fcff'
      : state.isFocused
      ? '#92c3fcff'
      : '#ffffff',
    color: state.isSelected ? '#000000ff' : state.isFocused ? '#ffffff' : '#000000',
    fontWeight: '500', 
    '&:hover': {
      backgroundColor: '#92c3fcff',
      color: '#000000ff',
    },
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 999,
    borderRadius: '0.5rem',
    overflow: 'hidden',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
};

// Normalize product data from API
/** Stable id per batch row — BatchCode/expiry can repeat for multiple ledger lines. */
const newBatchRowId = () => {
  try {
    const c = typeof window !== 'undefined' ? window.crypto : undefined;
    if (c && typeof c.randomUUID === 'function') {
      return c.randomUUID();
    }
  } catch {
    /* ignore */
  }
  return `bl-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
};

const normalizeProductData = (items = []) =>
  items.map((item) => {
    const itemNo = item.Item_No ?? item.ItemNo ?? item.id ?? null;
    const itemName = item.Item_Name ?? item.ItemName ?? '';
    const packageValue = item.package ?? item.Package ?? '';
    const unitValue = item.unit ?? item.Unit ?? '';

    return {
      ...item,
      id: item.id ?? itemNo,
      Item_No: itemNo,
      Item_Name: itemName,
      ItemName: item.ItemName ?? itemName,
      package: packageValue,
      Package: item.Package ?? packageValue,
      unit: unitValue,
      Unit: item.Unit ?? unitValue,
      Company: item.Company ?? item.company ?? '',
      Chemical: item.Chemical ?? item.chemical ?? '',
      TaxCode: item.TaxCode ?? item.taxCode ?? '',
      Rate: item.Rate ?? item.rate ?? 0,
      MRP: item.MRP ?? item.mrp ?? 0,
    };
  });

const StockAdjustment = () => {
  const [loading, setLoading] = useState(false);
  const [adjustmentNumber, setAdjustmentNumber] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [batches, setBatches] = useState([]);
  const [note, setNote] = useState('');
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [productsMaster, setProductsMaster] = useState([]);
  const [fetchingBatches, setFetchingBatches] = useState(false);
  const productSearchDebounceRef = useRef(null);

  const token = localStorage.getItem('accessToken');

  // Refs
  const productRef = useRef(null);
  const noteRef = useRef(null);
  const submitbtnRef = useRef(null);
  const plusStockRefs = useRef([]);
  const minusStockRefs = useRef([]);

  // Initial load - fetch first 1000 products
  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${BASE_URL}${GET_ITEM_MASTER}`, {
          headers: {
            'XApiKey': process.env.REACT_APP_API_KEY,
            'Authorization': `Bearer ${token}`,
            'PageNo': 1,
            'PageSize': 1000,
            'FilterText': '',
            'FilterColumn': '0',
            'FilterValue': '0',
          },
        });

        if (response.status === 200) {
          const masterData = response.data?.data?.masterData || [];
          const pharmacyItems = masterData.filter(
            (item) => (item.ItemType ?? item.itemType) === 'Pharmacy'
          );
          setProductsMaster(normalizeProductData(pharmacyItems));
        }
      } catch (error) {
        console.error('Failed to fetch products:', error);
        toast.error('Could not load products', {
          autoClose: 1500,
          icon: <FiAlertCircle className="text-red-500" />
        });
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchProducts();
    }
  }, [token]);

  // Load products from API based on search text (for AsyncSelect)
  const loadProductOptions = (inputValue, callback) => {
    // Clear previous debounce timeout
    if (productSearchDebounceRef.current) {
      clearTimeout(productSearchDebounceRef.current);
    }

    // If input is less than 2 characters, return initial products
    if (!inputValue || inputValue.length < 2) {
      const options = (productsMaster || []).map((product) => ({
        value: product.id,
        label: product.Item_Name ?? product.ItemName ?? '',
        genericName: product.Chemical ?? product.chemical ?? '',
        manufacturer: product.Company ?? product.company ?? '',
        ...product,
      }));
      callback(options);
      return;
    }

    // Debounce API call
    productSearchDebounceRef.current = setTimeout(async () => {
      try {
        const response = await axios.get(`${BASE_URL}${GET_ITEM_MASTER}`, {
          headers: {
            'XApiKey': process.env.REACT_APP_API_KEY,
            'Authorization': `Bearer ${token}`,
            'PageNo': 1,
            'PageSize': 1000,
            'FilterText': inputValue,
            'FilterColumn': '0',
            'FilterValue': '0',
          },
        });

        if (response.status === 200) {
          const masterData = response.data?.data?.masterData || [];
          const pharmacyItems = masterData.filter(
            (item) => (item.ItemType ?? item.itemType) === 'Pharmacy'
          );
          const normalizedProducts = normalizeProductData(pharmacyItems);
          
          const options = normalizedProducts.map((product) => ({
            value: product.id,
            label: product.Item_Name ?? product.ItemName ?? '',
            genericName: product.Chemical ?? product.chemical ?? '',
            manufacturer: product.Company ?? product.company ?? '',
            ...product,
          }));
          
          callback(options);
        } else {
          callback([]);
        }
      } catch (error) {
        console.error('Failed to fetch products:', error);
        callback([]);
      }
    }, 300); // 300ms debounce
  };

  // Get selected product option for AsyncSelect value prop
  const getSelectedProductOption = (productId) => {
    if (!productId) return null;
    const product = productsMaster.find(p => p.id === productId);
    if (!product) return null;
    return {
      value: product.id,
      label: product.Item_Name ?? product.ItemName ?? '',
      genericName: product.Chemical ?? product.chemical ?? '',
      manufacturer: product.Company ?? product.company ?? '',
      ...product,
    };
  };

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (productSearchDebounceRef.current) {
        clearTimeout(productSearchDebounceRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleWheel = (e) => {
      if (e.target.type === 'number') {
        e.target.blur();
      }
    };

    const handleKeyDown = (e) => {
      if (e.target.type === 'number') {
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
          e.preventDefault();
        }
      }
    };

    document.addEventListener('wheel', handleWheel, { capture: true });
    document.addEventListener('keydown', handleKeyDown, { capture: true });

    return () => {
      document.removeEventListener('wheel', handleWheel, { capture: true });
      document.removeEventListener('keydown', handleKeyDown, { capture: true });
    };
  }, []);

  useEffect(() => {
    const now = new Date();
    
    // Generate adjustment number
    setAdjustmentNumber(`SA${String(now.getFullYear()).slice(-2)}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}001`);
  }, []);

  // Fetch batches for selected product
  const fetchBatchesForProduct = async (productId, product = null) => {
    console.log('productId', productId);
    setFetchingBatches(true);
    try {
      // Get product details for default rate, mrp, and tax
      const response = await axios.get(`${BASE_URL}${GET_PRODUCT_BATCHES}`, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`,
        },
        params: {
          ItemNo: productId,
          DepartmentID: localStorage.getItem('department_id') || ''
        },
      });

      if (response.status === 200) {
        // Handle both response.data.data and response.data (direct array)
        const batchData = response.data?.data || response.data || [];

        console.log('batchData', batchData);
        
        // Map batches to expected format
        const productBatches = batchData
          .filter(batch => String(batch.Item_No ?? batch.ItemNo ?? '') === String(productId))
          .map(batch => {
            const currentStock = parseFloat(batch.Onhand ?? batch.onhand ?? 0);
            // Use batch-specific values if available, otherwise set to null
            const rate = batch.PurchasePrice ?? batch.purchasePrice ?? null;
            const mrp = batch.SalesPrice ?? batch.salesPrice ?? null;
            const tax = batch.TaxCode ?? batch.taxCode ?? null;
            
            return {
              batchLineId: newBatchRowId(),
              batchNo: batch.BatchCode ?? batch.Batch_Code ?? batch.batchCode ?? '',
              expiryDate: batch.ExpiryDate ?? batch.Expiry_Date ?? batch.expiryDate ?? '',
              currentStock: currentStock,
              rate: rate !== null ? String(rate) : null,
              mrp: mrp !== null ? String(mrp) : null,
              tax: tax !== null ? parseFloat(tax) : null,
              plusStock: 0,
              minusStock: 0,
              adjustedStock: currentStock
            };
          });

        setBatches(productBatches);
        
        // Initialize refs
        setTimeout(() => {
          plusStockRefs.current = new Array(productBatches.length).fill(null);
          minusStockRefs.current = new Array(productBatches.length).fill(null);
        }, 100);
      } else {
        setBatches([]);
        toast.error('Could not load batches for this product', {
          autoClose: 1500,
          icon: <FiAlertCircle className="text-red-500" />
        });
      }
    } catch (error) {
      console.error('Failed to fetch batches:', error);
      setBatches([]);
      toast.error('Could not load batches for this product', {
        autoClose: 1500,
        icon: <FiAlertCircle className="text-red-500" />
      });
    } finally {
      setFetchingBatches(false);
    }
  };

  // Handle product selection
  const handleProductChange = async (selectedOption) => {
    if (!selectedOption) {
      setSelectedProduct(null);
      setBatches([]);
      return;
    }
    
    // Use selectedOption directly (it contains all product data from AsyncSelect)
    const product = selectedOption;
    
    // Add to productsMaster if not already present (for getSelectedProductOption to work)
    if (product && !productsMaster.find(p => p.id === product.id)) {
      setProductsMaster(prev => [...prev, product]);
    }
    
    if (product) {
      console.log('product', product);
      setSelectedProduct(product);
      setBatches([]); // Clear previous batches
      // Fetch batches for the selected product
      await fetchBatchesForProduct(product.Item_No ?? product.id, product);
    } else {
      setSelectedProduct(null);
      setBatches([]);
      toast.error('Product not found', {
        autoClose: 1000,
        icon: <FiAlertCircle className="text-red-500" />
      });
    }
  };

  // Validate numeric input (allows decimals and empty string, only positive numbers)
  const isValidNumericInput = (value) => {
    if (value === '' || value === null || value === undefined) return true;
    // Allow only positive numbers with optional decimal point and digits
    const numericRegex = /^\d*\.?\d*$/;
    return numericRegex.test(value);
  };

  // Handle plus stock change (key by batchLineId so duplicate BatchCode rows stay independent)
  const handlePlusStockChange = (batchLineId, value) => {
    // Validate input is numeric
    if (!isValidNumericInput(value)) {
      toast.error('Please enter a valid number', { autoClose: 1500 });
      return;
    }

    setBatches((prev) =>
      prev.map((batch) => {
        if (batch.batchLineId !== batchLineId) return batch;
        const plusStock = parseFloat(value || 0);
        const minusStock = parseFloat(batch.minusStock || 0);
        const adjustedStock = batch.currentStock + plusStock - minusStock;
        return {
          ...batch,
          plusStock: value,
          adjustedStock: adjustedStock
        };
      })
    );
  };

  // Handle minus stock change
  const handleMinusStockChange = (batchLineId, value) => {
    // Validate input is numeric
    if (!isValidNumericInput(value)) {
      toast.error('Please enter a valid number', { autoClose: 1500 });
      return;
    }

    setBatches((prev) =>
      prev.map((batch) => {
        if (batch.batchLineId !== batchLineId) return batch;
        const plusStock = parseFloat(batch.plusStock || 0);
        const minusStock = parseFloat(value || 0);
        const adjustedStock = batch.currentStock + plusStock - minusStock;
        return {
          ...batch,
          minusStock: value,
          adjustedStock: adjustedStock
        };
      })
    );
  };

  // Handle Rate change
  const handleRateChange = (batchLineId, value) => {
    // Validate input is numeric
    if (!isValidNumericInput(value)) {
      toast.error('Please enter a valid number', { autoClose: 1500 });
      return;
    }

    setBatches((prev) =>
      prev.map((batch) =>
        batch.batchLineId === batchLineId
          ? { ...batch, rate: value === '' ? null : value }
          : batch
      )
    );
  };

  // Handle MRP change
  const handleMrpChange = (batchLineId, value) => {
    // Validate input is numeric
    if (!isValidNumericInput(value)) {
      toast.error('Please enter a valid number', { autoClose: 1500 });
      return;
    }

    setBatches((prev) =>
      prev.map((batch) =>
        batch.batchLineId === batchLineId
          ? { ...batch, mrp: value === '' ? null : value }
          : batch
      )
    );
  };

  // Handle Expiry Date change (date input format YYYY-MM-DD)
  const handleExpiryDateChange = (batchLineId, value) => {
    setBatches((prev) =>
      prev.map((batch) =>
        batch.batchLineId === batchLineId ? { ...batch, expiryDate: value || '' } : batch
      )
    );
  };

  // Format date for input (YYYY-MM-DD for date input type)
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    try {
      // If already in YYYY-MM-DD format, return as is
      if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return dateString;
      }
      
      // Try to parse as date and convert to YYYY-MM-DD
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
      
      // Try parsing as MM-YY format and convert to YYYY-MM-DD (first day of month)
      if (dateString.match(/^\d{2}-\d{2}$/)) {
        const [month, year] = dateString.split('-');
        const fullYear = `20${year}`; // Assuming 2000s
        return `${fullYear}-${month}-01`;
      }
      
      return '';
    } catch (error) {
      return '';
    }
  };

  // Handle key press to prevent invalid characters
  const handleNumericKeyPress = (e) => {
    const char = e.key;
    // Allow: numbers, decimal point, backspace, delete, tab, arrow keys, enter
    if (!/[\d.]/.test(char) && !['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Enter'].includes(char)) {
      e.preventDefault();
    }
    // Prevent multiple decimal points
    if (char === '.' && e.target.value.includes('.')) {
      e.preventDefault();
    }
  };

  // Handle plus stock key navigation
  const handlePlusStockKeyDown = (e, index) => {
    handleNumericKeyPress(e);
    if (e.key === 'Enter') {
      e.preventDefault();
      if (minusStockRefs.current[index]) {
        minusStockRefs.current[index].focus();
        minusStockRefs.current[index].select();
      } else if (index < batches.length - 1 && plusStockRefs.current[index + 1]) {
        plusStockRefs.current[index + 1].focus();
        plusStockRefs.current[index + 1].select();
      } else {
        noteRef.current?.focus();
      }
    }
  };

  // Handle minus stock key navigation
  const handleMinusStockKeyDown = (e, index) => {
    handleNumericKeyPress(e);
    if (e.key === 'Enter') {
      e.preventDefault();
      if (index < batches.length - 1 && plusStockRefs.current[index + 1]) {
        plusStockRefs.current[index + 1].focus();
        plusStockRefs.current[index + 1].select();
      } else {
        noteRef.current?.focus();
      }
    }
  };

  const customProductOptionLabel = ({ label, genericName, manufacturer }) => (
    <div className="flex flex-col">
      <span className="font-bold text-black text-sm">{label}</span>
    </div>
  );

  const resetForm = () => {
    setSelectedProduct(null);
    setBatches([]);
    setNote('');
    if (productRef.current) {
      productRef.current.focus();
    }
  };

  const handleSave = async () => {
    setIsButtonDisabled(true);
    setLoading(true);

    if (!selectedProduct) {
      toast.error('Please select a product.', { autoClose: 1500 });
      setLoading(false);
      setIsButtonDisabled(false);
      productRef.current?.focus();
      return;
    }

    if (!batches || batches.length === 0) {
      toast.error('No batches available for this product.', { autoClose: 1500 });
      setLoading(false);
      setIsButtonDisabled(false);
      return;
    }

    // Validate all inputs are numeric
    const invalidBatches = [];
    batches.forEach((batch, index) => {
      const plusStock = batch.plusStock?.toString().trim() || '';
      const minusStock = batch.minusStock?.toString().trim() || '';
      
      if (plusStock && !isValidNumericInput(plusStock)) {
        invalidBatches.push({ index, field: 'Plus Stock', batchNo: batch.batchNo || `Batch ${index + 1}` });
      }
      if (minusStock && !isValidNumericInput(minusStock)) {
        invalidBatches.push({ index, field: 'Minus Stock', batchNo: batch.batchNo || `Batch ${index + 1}` });
      }
    });

    if (invalidBatches.length > 0) {
      toast.error(`Invalid numeric value in ${invalidBatches[0].field} for ${invalidBatches[0].batchNo}`, { autoClose: 2000 });
      setLoading(false);
      setIsButtonDisabled(false);
      // Focus on the first invalid field
      if (invalidBatches[0].field === 'Plus Stock' && plusStockRefs.current[invalidBatches[0].index]) {
        plusStockRefs.current[invalidBatches[0].index].focus();
      } else if (minusStockRefs.current[invalidBatches[0].index]) {
        minusStockRefs.current[invalidBatches[0].index].focus();
      }
      return;
    }

    // Check if any batch will go to negative stock (only if minus stock is entered)
    const negativeStockBatches = [];
    batches.forEach((batch, index) => {
      const plusQty = parseFloat(batch.plusStock || 0);
      const minusQty = parseFloat(batch.minusStock || 0);
      
      // Only validate negative stock if minus stock is actually entered
      if (minusQty > 0) {
        const adjustedStock = batch.currentStock + plusQty - minusQty;
        
        if (adjustedStock < 0) {
          negativeStockBatches.push({
            batchNo: batch.batchNo || `Batch ${index + 1}`,
            currentStock: batch.currentStock,
            adjustedStock: adjustedStock
          });
        }
      }
    });

    if (negativeStockBatches.length > 0) {
      const batchInfo = negativeStockBatches[0];
      toast.error(
        `Stock cannot go negative! ${batchInfo.batchNo} will have ${batchInfo.adjustedStock} stock (Current: ${batchInfo.currentStock})`, 
        { autoClose: 3000 }
      );
      setLoading(false);
      setIsButtonDisabled(false);
      return;
    }

    // Prepare data according to API structure
    const now = new Date();
    // Use local timezone instead of UTC to preserve IST date
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const entryDate = `${year}-${month}-${day}`; // Format: YYYY-MM-DD (in local timezone)

    const items = batches.map((batch, index) => {
        const plusQty = parseFloat(batch.plusStock || 0);
        const minusQty = parseFloat(batch.minusStock || 0);
        const adjustedQty = batch.currentStock + plusQty - minusQty;

        // Format expiry date properly (YYYY-MM-DD format)
        let formattedExpiryDate = '';
        if (batch.expiryDate) {
          try {
            // If already in YYYY-MM-DD format, use it directly
            if (batch.expiryDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
              formattedExpiryDate = batch.expiryDate.substring(0, 10);
            } else {
              // Otherwise, try to parse and format
              const date = new Date(batch.expiryDate);
              if (!isNaN(date.getTime())) {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                formattedExpiryDate = `${year}-${month}-${day}`;
              }
            }
          } catch (error) {
            console.error('Error formatting expiry date:', error);
          }
        }

        return {
          SiNo: index + 1,
          ItemNo: parseInt(selectedProduct.Item_No) || parseInt(selectedProduct.id) || 0,
          
          BatchNo: batch.batchNo || '',
          ExpiryDate: formattedExpiryDate,
          Mrp: batch.mrp !== null && batch.mrp !== undefined ? parseFloat(batch.mrp) : 0,
          Rate: batch.rate !== null && batch.rate !== undefined ? parseFloat(batch.rate) : 0,
          PurchasePrice: batch.rate !== null && batch.rate !== undefined ? parseFloat(batch.rate) : 0,
          CurrentStock: parseFloat(batch.currentStock || 0),
          PlusQty: plusQty,
          MinusQty: minusQty,
          AdjustedQty: adjustedQty,
          Taxcode: batch.tax !== null && batch.tax !== undefined ? String(batch.tax) : '',
          DepartmentID: localStorage.getItem('department_id') || ''
        };
      });

    const requestData = {
      EntryDate: entryDate,
      Notes: note || '',
      ProductName: selectedProduct.Item_Name || selectedProduct.ItemName || '',
      Items: items
    };

    console.log('Stock Adjustment Request Data:', requestData);

    setIsButtonDisabled(false);

    try {

      const response = await axios.post(`${BASE_URL}${POST_STOCK_ADJUSTMENT}`, requestData, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.status === 200 || response.status === 201) {
        toast.success('Stock Adjustment Saved Successfully', { autoClose: 2000 });
        
        setTimeout(() => {
          resetForm();
          setLoading(false);
          setIsButtonDisabled(false);
        }, 2000);
      } else {
        throw new Error('Unexpected response status');
      }
    } catch (error) {
      console.error('Failed to save stock adjustment:', error);
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to save stock adjustment. Please try again.';
      toast.error(errorMessage, { autoClose: 3000 });
      setLoading(false);
      setIsButtonDisabled(false);
    }
  };

  return (
    <div className='flex h-full bg-background'>
      {/* <LeftMenu /> */}
      <ToastContainer
        position="top-center"
        autoClose={1000}
        limit={1}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Slide}
      />
          
      {loading && <Spinner />}

      <div className='flex-1 flex h-full flex-col overflow-auto hide-scrollbar'>

        <div className="bg-white p-3 mb-3">
          <div>
            <h1 className="text-lg font-bold text-gray-800 flex items-center">
              Stock Adjustment
            </h1>
          </div>
        </div>

        <div className="bg-white ml-5 mr-5 rounded-lg shadow-sm p-3 mb-3">
          <div className='w-full flex flex-row gap-3'>
            <div className='w-full flex p-3 bg-gray-50 rounded-lg border border-gray-200 flex-col gap-2.5'>
              {/* Adjustment Info Section */}
              <div className="flex items-center gap-2 pb-2 border-b border-black border-1">
                <FiPackage className="text-indigo-600" size={16} />
                <h3 className="font-semibold text-gray-800 text-sm">Adjustment Information</h3>
              </div>
              
              <div className='w-full'>
                <div className='grid grid-cols-12 gap-2.5'>

                  {/* Product Selection */}
                  <div className='col-span-12'>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Select Product <span className="text-red-500">*</span>
                    </label>
                    <AsyncSelect
                      loadOptions={loadProductOptions}
                      className="w-full"
                      isSearchable
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      defaultOptions={true}
                      cacheOptions={true}
                      value={getSelectedProductOption(selectedProduct?.id)}
                      formatOptionLabel={customProductOptionLabel}
                      onChange={handleProductChange}
                      onMenuClose={() => {
                        if (selectedProduct && batches.length > 0 && plusStockRefs.current[0]) {
                          setTimeout(() => {
                            plusStockRefs.current[0].focus();
                          }, 100);
                        } else {
                          noteRef.current?.focus();
                        }
                      }}
                      isClearable
                      noOptionsMessage={({ inputValue }) => 
                        inputValue && inputValue.length < 2 
                          ? 'Type at least 2 characters to search' 
                          : 'No products found'
                      }
                      loadingMessage={() => 'Searching products...'}
                      styles={customSelectStyles}
                      ref={productRef}
                    />
                  </div>

                  {/* Note */}
                  <div className='col-span-12'>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Note
                    </label>
                    <input
                      type="text"
                      className="w-full p-2.5 border border-black border-1 font-medium bg-white rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-colors text-xs"
                      ref={noteRef}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          submitbtnRef.current?.focus();
                        }
                      }}
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder="Enter reason for adjustment..."
                    />
                  </div>
                  
                </div>
              </div>
            </div>
            
            {/* Summary Sidebar */}
            <div className='w-80 flex-shrink-0'>
              <div className="bg-gray-50 p-3 rounded-lg shadow-sm border border-gray-300 flex flex-col h-full">
                <div className="flex justify-between items-center mb-2 pb-2 border-b border-gray-400">
                  <h2 className="font-semibold text-gray-800 text-sm">Summary</h2>
                  <span className="text-lg font-bold text-gray-700">{adjustmentNumber}</span>
                </div>
                
                {selectedProduct && (
                  <div className="bg-white p-2 rounded-md mb-2.5 border border-gray-300">
                    <div className="text-xs font-medium text-gray-700">
                      {selectedProduct.Item_Name ?? selectedProduct.ItemName ?? ''}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {fetchingBatches ? 'Loading batches...' : `${batches.length} batch(es)`}
                    </div>
                  </div>
                )}

                <div className="space-y-1.5 flex-grow text-xs">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-gray-600">Total Plus Stock</span>
                    <span className="font-semibold text-green-600">
                      +{batches.reduce((sum, batch) => sum + parseFloat(batch.plusStock || 0), 0)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-gray-600">Total Minus Stock</span>
                    <span className="font-semibold text-red-600">
                      -{batches.reduce((sum, batch) => sum + parseFloat(batch.minusStock || 0), 0)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-1.5 border-t border-b border-gray-400 my-1.5">
                    <span className="font-semibold text-gray-800">Net Adjustment</span>
                    <span className="font-bold text-base text-black">
                      {batches.reduce((sum, batch) => sum + parseFloat(batch.plusStock || 0) - parseFloat(batch.minusStock || 0), 0) > 0 ? '+' : ''}
                      {batches.reduce((sum, batch) => sum + parseFloat(batch.plusStock || 0) - parseFloat(batch.minusStock || 0), 0)}
                    </span>
                  </div>
                </div>
                
                <div className="flex flex-col space-y-2 mt-3">
                  <div className="flex justify-between gap-2">                    
                    <button 
                      type="button" 
                      className="flex-1 py-2 px-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-medium text-xs rounded-md hover:from-indigo-700 hover:to-purple-700 shadow-sm transition-all"
                      onClick={handleSave} 
                      disabled={isButtonDisabled}
                      ref={submitbtnRef}
                    >
                      Submit
                    </button>
                    <button 
                      type="button" 
                      className="flex-1 py-2 px-3 bg-btnblue text-white font-medium text-xs rounded-md hover:bg-indigo-700 shadow-sm transition-all"
                      onClick={resetForm}
                    >
                      New +
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Batch Adjustment Table */}
          {fetchingBatches && selectedProduct && (
            <div className="mt-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">Loading batches...</p>
            </div>
          )}

          {selectedProduct && !fetchingBatches && batches.length > 0 && (
            <div className="mt-3">
              <div className="flex justify-between items-center mb-2 pb-1.5 border-b border-gray-300">
                <h3 className="font-semibold text-gray-800 text-sm">Batch-wise Stock Adjustment</h3>
              </div>
                      
              <div className="overflow-x-auto rounded-lg border border-gray-400">
                <table className="w-full border-collapse">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="p-3 text-left text-xs font-medium text-black uppercase tracking-wider">No.</th>
                      <th className="p-3 text-left text-xs font-medium text-black uppercase tracking-wider">Batch No</th>
                      <th className="p-3 text-left text-xs font-medium text-black uppercase tracking-wider">Expiry Date</th>
                      <th className="p-3 text-right text-xs font-medium text-black uppercase tracking-wider">Rate</th>
                      <th className="p-3 text-right text-xs font-medium text-black uppercase tracking-wider">MRP</th>
                      <th className="p-3 text-right text-xs font-medium text-black uppercase tracking-wider">Current Stock</th>
                      <th className="p-3 text-left text-xs font-medium text-black uppercase tracking-wider">Plus Stock</th>
                      <th className="p-3 text-left text-xs font-medium text-black uppercase tracking-wider">Minus Stock</th>
                      <th className="p-3 text-right text-xs font-medium text-black uppercase tracking-wider">Adjusted Stock</th>
                    </tr>
                  </thead>
                  
                  <tbody className="bg-white divide-y divide-gray-200">
                    {batches.map((batch, index) => (
                      <tr 
                        key={batch.batchLineId} 
                        className="hover:bg-gray-50 transition-colors"
                      >                
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                          {index + 1}
                        </td>
                        
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700 font-medium">
                          {batch.batchNo || 'N/A'}
                        </td>

                        <td className="px-3 py-2 whitespace-nowrap text-sm">
                          <input
                            type="date"
                            className="w-full px-2 py-1.5 border border-gray-300 text-black bg-white rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 transition-colors text-xs"
                            value={formatDateForInput(batch.expiryDate)}
                            onChange={(e) => handleExpiryDateChange(batch.batchLineId, e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                // Focus Rate of current row
                                const rateInput = document.querySelector(`input[data-batch-rate="${index}"]`);
                                if (rateInput) {
                                  rateInput.focus();
                                  rateInput.select();
                                }
                              }
                            }}
                            data-batch-expiry={index}
                          />
                        </td>

                        <td className="px-3 py-2 whitespace-nowrap text-sm">
                          <input
                            type="text"
                            className="w-full px-2 py-1.5 border border-gray-300 text-black bg-white rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 transition-colors text-right font-medium text-xs"
                            value={batch.rate !== null && batch.rate !== undefined ? batch.rate : ''}
                            onChange={(e) => handleRateChange(batch.batchLineId, e.target.value)}
                            onKeyDown={(e) => {
                              handleNumericKeyPress(e);
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                // Focus MRP of current row
                                const mrpInput = document.querySelector(`input[data-batch-mrp="${index}"]`);
                                if (mrpInput) {
                                  mrpInput.focus();
                                  mrpInput.select();
                                }
                              }
                            }}
                            onPaste={(e) => {
                              e.preventDefault();
                              const pastedText = (e.clipboardData || window.clipboardData).getData('text');
                              if (isValidNumericInput(pastedText)) {
                                handleRateChange(batch.batchLineId, pastedText);
                              } else {
                                toast.error('Invalid numeric value pasted', { autoClose: 1500 });
                              }
                            }}
                            data-batch-rate={index}
                            placeholder="0.00"
                            onFocus={(e) => e.target.select()}
                          />
                        </td>

                        <td className="px-3 py-2 whitespace-nowrap text-sm">
                          <input
                            type="text"
                            className="w-full px-2 py-1.5 border border-gray-300 text-black bg-white rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 transition-colors text-right font-medium text-xs"
                            value={batch.mrp !== null && batch.mrp !== undefined ? batch.mrp : ''}
                            onChange={(e) => handleMrpChange(batch.batchLineId, e.target.value)}
                            onKeyDown={(e) => {
                              handleNumericKeyPress(e);
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                if (index < batches.length - 1) {
                                  // Focus expiry date of next row
                                  const nextExpiryInput = document.querySelector(`input[data-batch-expiry="${index + 1}"]`);
                                  if (nextExpiryInput) {
                                    nextExpiryInput.focus();
                                  }
                                } else {
                                  // Focus first plus stock
                                  if (plusStockRefs.current[0]) {
                                    plusStockRefs.current[0].focus();
                                    plusStockRefs.current[0].select();
                                  }
                                }
                              }
                            }}
                            onPaste={(e) => {
                              e.preventDefault();
                              const pastedText = (e.clipboardData || window.clipboardData).getData('text');
                              if (isValidNumericInput(pastedText)) {
                                handleMrpChange(batch.batchLineId, pastedText);
                              } else {
                                toast.error('Invalid numeric value pasted', { autoClose: 1500 });
                              }
                            }}
                            data-batch-mrp={index}
                            placeholder="0.00"
                            onFocus={(e) => e.target.select()}
                          />
                        </td>

                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700 font-medium text-center">
                          {batch.currentStock}
                        </td>

                        <td className="px-3 py-2 whitespace-nowrap text-sm" style={{ minWidth: '90px', maxWidth: '100px' }}>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 flex items-center pl-2 pointer-events-none text-green-600">
                              <FiPlus size={14} />
                            </div>
                            <input
                              type="text"
                              className="w-full pl-7 pr-2 py-1.5 border border-green-300 text-black bg-white rounded-lg focus:ring-2 focus:ring-green-200 focus:border-green-500 transition-colors text-right font-medium text-xs"
                              value={batch.plusStock || ''}
                              onChange={(e) => handlePlusStockChange(batch.batchLineId, e.target.value)}
                              onKeyDown={(e) => handlePlusStockKeyDown(e, index)}
                              onPaste={(e) => {
                                e.preventDefault();
                                const pastedText = (e.clipboardData || window.clipboardData).getData('text');
                                if (isValidNumericInput(pastedText)) {
                                  handlePlusStockChange(batch.batchLineId, pastedText);
                                } else {
                                  toast.error('Invalid numeric value pasted', { autoClose: 1500 });
                                }
                              }}
                              ref={(el) => (plusStockRefs.current[index] = el)}
                              placeholder="0"
                              onFocus={(e) => e.target.select()}
                            />
                          </div>
                        </td>

                        <td className="px-3 py-2 whitespace-nowrap text-sm" style={{ minWidth: '90px', maxWidth: '100px' }}>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 flex items-center pl-2 pointer-events-none text-red-600">
                              <FiMinus size={14} />
                            </div>
                            <input
                              type="text"
                              className="w-full pl-7 pr-2 py-1.5 border border-red-300 text-black bg-white rounded-lg focus:ring-2 focus:ring-red-200 focus:border-red-500 transition-colors text-right font-medium text-xs"
                              value={batch.minusStock || ''}
                              onChange={(e) => handleMinusStockChange(batch.batchLineId, e.target.value)}
                              onKeyDown={(e) => handleMinusStockKeyDown(e, index)}
                              onPaste={(e) => {
                                e.preventDefault();
                                const pastedText = (e.clipboardData || window.clipboardData).getData('text');
                                if (isValidNumericInput(pastedText)) {
                                  handleMinusStockChange(batch.batchLineId, pastedText);
                                } else {
                                  toast.error('Invalid numeric value pasted', { autoClose: 1500 });
                                }
                              }}
                              ref={(el) => (minusStockRefs.current[index] = el)}
                              placeholder="0"
                              onFocus={(e) => e.target.select()}
                            />
                          </div>
                        </td>

                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700 font-bold text-right">
                          <span className={`px-2 py-1 rounded ${
                            batch.adjustedStock > batch.currentStock 
                              ? 'bg-green-100 text-green-800' 
                              : batch.adjustedStock < batch.currentStock 
                              ? 'bg-red-100 text-red-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {batch.adjustedStock}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {selectedProduct && !fetchingBatches && batches.length === 0 && (
            <div className="mt-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">No batches available for this product.</p>
            </div>
          )}

          {!selectedProduct && (
            <div className="mt-3 p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <p className="text-sm text-gray-600 text-center">Please select a product to view and adjust batches.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default withAuth(StockAdjustment);

